/*
 * <oilogic-variant-select> — variant picker for the main product section.
 * On change it re-fetches the section via the Section Rendering API and syncs
 * the parts that depend on the selected variant: price row, add-to-cart
 * button state/label, sticky bar, and the installments banner. Falls back to
 * a full navigation if the fetch fails, so the page never shows a stale
 * price. IIFE + guard follow the component-card-product.js pattern in case
 * another module loads this file again.
 */
(() => {
  class OilogicVariantSelect extends HTMLElement {
    #ctrl = new AbortController();
    #fetchCtrl = null;

    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select || !this.dataset.sectionId || !this.dataset.url) return;
      this.select.addEventListener('change', () => this.onChange(), { signal: this.#ctrl.signal });
    }

    disconnectedCallback() {
      this.#ctrl.abort();
      this.#fetchCtrl?.abort();
    }

    async onChange() {
      const variantId = this.select.value;
      const pageUrl = new URL(this.dataset.url, window.location.origin);
      pageUrl.searchParams.set('variant', variantId);

      const requestUrl = new URL(pageUrl);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      this.#fetchCtrl?.abort();
      const ctrl = new AbortController();
      this.#fetchCtrl = ctrl;
      this.setAttribute('data-loading', '');

      try {
        const response = await fetch(requestUrl, { signal: ctrl.signal });
        if (!response.ok) throw new Error(`variant section render failed (${response.status})`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        this.#swapPrices(html);
        this.#syncSubmitButton(html);
        this.#syncStickyBar(html);
        this.#syncInstallments(variantId);

        history.replaceState({}, '', pageUrl);
        this.dispatchEvent(
          new CustomEvent('oilogic:variant:change', {
            bubbles: true,
            composed: true,
            detail: { variantId: Number(variantId) },
          })
        );
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Full reload keeps price/state consistent when the partial fails.
        window.location.href = pageUrl;
      } finally {
        if (this.#fetchCtrl === ctrl) this.removeAttribute('data-loading');
      }
    }

    // Price rows carry no JS refs, so whole-node swaps are safe and keep the
    // compare-at/savings markup in sync without duplicating money formatting.
    #swapPrices(html) {
      const selectors = ['.main-product__info .product-price', '.sticky-add-to-cart .product-price'];
      for (const selector of selectors) {
        const current = document.querySelector(selector);
        const next = html.querySelector(selector);
        if (current && next) current.replaceWith(next);
      }
    }

    // <oilogic-add-to-cart> caches the button/label nodes, so update them in
    // place instead of replacing them.
    #syncSubmitButton(html) {
      const button = document.querySelector('.product-buy__submit');
      const next = html.querySelector('.product-buy__submit');
      if (!button || !next) return;

      button.toggleAttribute('disabled', next.hasAttribute('disabled'));
      const label = button.querySelector('[data-atc-label]');
      const nextLabel = next.querySelector('[data-atc-label]');
      if (label && nextLabel) {
        label.textContent = nextLabel.textContent;
        // Reset the "Added" flash baseline so it restores the new label.
        delete label.dataset.original;
      }
    }

    #syncStickyBar(html) {
      const sticky = document.querySelector('oilogic-sticky-add-to-cart');
      const next = html.querySelector('oilogic-sticky-add-to-cart');
      if (!sticky || !next) return;

      sticky.dataset.variantId = next.dataset.variantId;
      const button = sticky.querySelector('[data-submit-button]');
      const nextButton = next.querySelector('[data-submit-button]');
      if (!button || !nextButton) return;

      button.toggleAttribute('disabled', nextButton.hasAttribute('disabled'));
      const label = button.querySelector('.sticky-add-to-cart__label');
      const nextLabel = nextButton.querySelector('.sticky-add-to-cart__label');
      if (label && nextLabel) label.textContent = nextLabel.textContent;
    }

    // The installments banner lives in its own hidden product form; Shopify's
    // payment-terms widget re-renders when its id input emits a change event.
    #syncInstallments(variantId) {
      document.querySelectorAll('.product-installments [name="id"]').forEach((input) => {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  if (!customElements.get('oilogic-variant-select')) {
    customElements.define('oilogic-variant-select', OilogicVariantSelect);
  }
})();
